import { useEffect, useRef, useState, useCallback } from 'react';
import { MessageCircle, X, Trash2, Mail, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';

const SUPPORT_EMAIL = 'leah@mystep.ai';
import { useChatStore } from '../../stores/chatStore';
import { sendChatMessage, getPageInsights, type PageInsights } from '../../services/chat';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import type { ChatContext, ChatAction } from '@mise/shared';

interface ChatWidgetProps {
  context: Omit<ChatContext, 'restaurantId'>;
}

export default function ChatWidget({ context }: ChatWidgetProps) {
  const queryClient = useQueryClient();
  const {
    isOpen,
    messages,
    isLoading,
    error,
    toggleOpen,
    addMessage,
    updateMessage,
    clearMessages,
    setLoading,
    setError,
  } = useChatStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [insights, setInsights] = useState<PageInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const lastFetchedPage = useRef<string | null>(null);

  // Invalidate relevant queries based on actions taken by the AI
  const invalidateQueriesForActions = useCallback((actions: ChatAction[]) => {
    const queriesToInvalidate = new Set<string>();

    for (const action of actions) {
      switch (action.type) {
        case 'reservation_created':
        case 'reservation_updated':
        case 'reservation_cancelled':
          queriesToInvalidate.add('reservations');
          queriesToInvalidate.add('tables');
          queriesToInvalidate.add('sections'); // POS uses sections query
          queriesToInvalidate.add('host-overview');
          queriesToInvalidate.add('cover-forecast');
          queriesToInvalidate.add('optimization-suggestions');
          break;
        case 'guest_created':
        case 'guest_updated':
          queriesToInvalidate.add('guests');
          queriesToInvalidate.add('guests-all');
          break;
        case 'shift_updated':
        case 'shift_swap_created':
        case 'shift_swap_approved':
        case 'shift_swap_rejected':
          queriesToInvalidate.add('shift-instances');
          queriesToInvalidate.add('shift-swaps');
          queriesToInvalidate.add('coverage-gaps');
          break;
        case 'menu_item_updated':
          queriesToInvalidate.add('menu-items');
          queriesToInvalidate.add('specials');
          queriesToInvalidate.add('eighty-six');
          break;
        case 'inventory_updated':
          queriesToInvalidate.add('inventory');
          queriesToInvalidate.add('inventory-items');
          break;
        case 'waitlist_added':
        case 'waitlist_updated':
          queriesToInvalidate.add('waitlist');
          queriesToInvalidate.add('host-overview');
          queriesToInvalidate.add('tables');
          queriesToInvalidate.add('sections'); // POS
          break;
        case 'table_status_updated':
          queriesToInvalidate.add('tables');
          queriesToInvalidate.add('sections'); // POS uses sections query
          queriesToInvalidate.add('host-overview');
          queriesToInvalidate.add('floor-map');
          queriesToInvalidate.add('reservations'); // Table changes may affect reservation display
          queriesToInvalidate.add('checks');
          break;
        case 'order_created':
        case 'order_updated':
          queriesToInvalidate.add('orders');
          queriesToInvalidate.add('active-orders');
          queriesToInvalidate.add('kitchen-display');
          queriesToInvalidate.add('checks');
          break;
        case 'employee_created':
        case 'employee_updated':
          queriesToInvalidate.add('employees');
          queriesToInvalidate.add('staff');
          queriesToInvalidate.add('shift-instances');
          break;
        case 'check_updated':
          queriesToInvalidate.add('checks');
          queriesToInvalidate.add('tables');
          queriesToInvalidate.add('sections'); // POS
          queriesToInvalidate.add('host-overview');
          queriesToInvalidate.add('reservations');
          queriesToInvalidate.add('sales-summary');
          queriesToInvalidate.add('analytics');
          break;
      }
    }

    // Invalidate all matched queries
    for (const queryKey of queriesToInvalidate) {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    }

    // Also invalidate any queries that match partial keys
    if (queriesToInvalidate.size > 0) {
      console.log('[ChatWidget] Invalidated queries:', Array.from(queriesToInvalidate));
    }
  }, [queryClient]);

  // Fetch page-specific insights when chat opens or page changes
  const fetchInsights = useCallback(async () => {
    const page = context.currentPage || 'dashboard';
    if (context.type !== 'dashboard' || lastFetchedPage.current === page) return;

    setInsightsLoading(true);
    try {
      const result = await getPageInsights(page);
      if (result.success && result.data) {
        setInsights(result.data);
        lastFetchedPage.current = page;
      }
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    } finally {
      setInsightsLoading(false);
    }
  }, [context.currentPage, context.type]);

  // Fetch insights when chat opens
  useEffect(() => {
    if (isOpen && context.type === 'dashboard') {
      fetchInsights();
    }
  }, [isOpen, fetchInsights]);

  // Refresh insights when page changes while chat is open
  useEffect(() => {
    if (isOpen && context.type === 'dashboard' && context.currentPage !== lastFetchedPage.current) {
      lastFetchedPage.current = null; // Reset to allow re-fetch
      fetchInsights();
    }
  }, [context.currentPage, isOpen, fetchInsights]);

  // Context-aware configuration
  const getContextConfig = () => {
    if (context.type === 'onboarding') {
      const step = context.currentPage || '';
      if (step.includes('1')) {
        return {
          subtitle: 'Setup assistance',
          emptyMessage: 'I can help you fill in your restaurant details. Just ask!',
          initialSuggestions: ['What timezone should I use?', 'Why do you need my phone number?'],
        };
      } else if (step.includes('2')) {
        return {
          subtitle: 'Floor plan help',
          emptyMessage: 'Need help with your floor plan? I can explain how sections and tables work.',
          initialSuggestions: ['How do sections work?', 'How many tables should I add?'],
        };
      } else if (step.includes('3')) {
        return {
          subtitle: 'Menu setup help',
          emptyMessage: 'I can help you set up your menu quickly. Want tips on organizing categories?',
          initialSuggestions: ['Import menu from my website', 'How should I organize categories?'],
        };
      } else if (step.includes('4')) {
        return {
          subtitle: 'Staff setup help',
          emptyMessage: 'Setting up staff is easy! Each person gets a PIN to access the POS.',
          initialSuggestions: ['What roles are available?', 'How do PINs work?'],
        };
      } else if (step.includes('5')) {
        return {
          subtitle: 'Payment setup help',
          emptyMessage: 'I can answer questions about payment processing and Stripe.',
          initialSuggestions: ['Can I set up payments later?', 'How do tips work?'],
        };
      } else if (step.includes('6')) {
        return {
          subtitle: 'Plan selection help',
          emptyMessage: 'Not sure which plan is right? I can help you compare options.',
          initialSuggestions: ['Which plan is best for a small cafe?', 'Can I change plans later?'],
        };
      }
      return {
        subtitle: 'Setup assistance',
        emptyMessage: 'I\'m here to help you set up your restaurant. Ask me anything!',
        initialSuggestions: ['Help me get started', 'What do I need to set up?'],
      };
    }

    // Dashboard context - page-specific suggestions
    const page = context.currentPage || 'dashboard';

    switch (page) {
      case 'dashboard':
        return {
          subtitle: 'Operations insights',
          emptyMessage: 'I can give you a quick overview of how things are going today.',
          initialSuggestions: [
            'Give me today\'s insights',
            'How are sales compared to yesterday?',
            'Any issues I should know about?',
          ],
        };

      case 'inventory':
        return {
          subtitle: 'Inventory management',
          emptyMessage: 'I can help you manage inventory, check stock levels, and identify items to reorder.',
          initialSuggestions: [
            'What items are low on stock?',
            'Add a new inventory item',
            'Which items should I order today?',
          ],
        };

      case 'reservations':
        return {
          subtitle: 'Reservations help',
          emptyMessage: 'I can help you manage reservations, check availability, and optimize seating.',
          initialSuggestions: [
            'Show me today\'s reservations',
            'Create a reservation for tonight',
            'Do I have enough staff for tonight?',
          ],
        };

      case 'menu':
        return {
          subtitle: 'Menu management',
          emptyMessage: 'I can help you manage your menu, update prices, and set daily specials.',
          initialSuggestions: [
            'What are today\'s specials?',
            'Add a new menu item',
            '86 an item that\'s out of stock',
          ],
        };

      case 'staff':
        return {
          subtitle: 'Staff management',
          emptyMessage: 'I can help you manage staff schedules, track performance, and handle assignments.',
          initialSuggestions: [
            'Who\'s working today?',
            'Show staff performance this week',
            'Add a new employee',
          ],
        };

      case 'labor':
        return {
          subtitle: 'Labor & swap management',
          emptyMessage: 'I can help you manage shift swaps, coverage, and time-off requests.',
          initialSuggestions: [
            'Show pending swap requests',
            'Are there any shifts that need coverage?',
            'Swap John\'s shift tomorrow to Sarah',
          ],
        };

      case 'guests':
        return {
          subtitle: 'Guest management',
          emptyMessage: 'I can help you find guest info, check preferences, and view dining history.',
          initialSuggestions: [
            'Find a guest by name',
            'Who are my VIP guests?',
            'Show guests with allergies',
          ],
        };

      case 'analytics':
        return {
          subtitle: 'Analytics & insights',
          emptyMessage: 'I can help you understand your restaurant\'s performance and trends.',
          initialSuggestions: [
            'How did we do this week?',
            'Compare sales to last month',
            'Who are my top performers?',
          ],
        };

      case 'settings':
        return {
          subtitle: 'Settings help',
          emptyMessage: 'I can help you configure your restaurant settings and preferences.',
          initialSuggestions: [
            'Update restaurant info',
            'Change operating hours',
            'How do I manage sections?',
          ],
        };

      case 'floor':
        return {
          subtitle: 'Floor management',
          emptyMessage: 'I can help you manage tables, sections, and floor layout.',
          initialSuggestions: [
            'Show table availability',
            'Which tables are occupied?',
            'Suggest a table for party of 4',
          ],
        };

      case 'checks':
        return {
          subtitle: 'Check management',
          emptyMessage: 'I can help you with open checks, payments, and order tracking.',
          initialSuggestions: [
            'Show open checks',
            'What\'s the average check today?',
            'Any checks waiting on payment?',
          ],
        };

      default:
        return {
          subtitle: 'Restaurant assistant',
          emptyMessage: 'I\'m here to help with anything - sales, inventory, reservations, staff, and more!',
          initialSuggestions: [
            'Give me today\'s insights',
            'What should I focus on?',
            'Any issues to address?',
          ],
        };
    }
  };

  const contextConfig = getContextConfig();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && isOpen) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const handleSend = async (text: string) => {
    setError(null);
    setSuggestions([]);

    // Add user message
    const userMessageId = addMessage({
      text,
      sender: 'user',
      status: 'sent',
    });

    // Build conversation history from last 10 messages
    const history = messages.slice(-10).map((msg) => ({
      role: msg.sender as 'user' | 'assistant',
      content: msg.text,
    }));

    setLoading(true);

    // Add pending assistant message
    const assistantMessageId = addMessage({
      text: '',
      sender: 'assistant',
      status: 'sending',
    });

    try {
      const response = await sendChatMessage(text, context, history);

      if (response.success && response.data) {
        updateMessage(assistantMessageId, {
          text: response.data.response,
          status: 'sent',
        });

        if (response.data.suggestions) {
          setSuggestions(response.data.suggestions);
        }

        // Invalidate queries if the AI took any actions
        if (response.data.actionsTaken && response.data.actionsTaken.length > 0) {
          invalidateQueriesForActions(response.data.actionsTaken);
        }
      } else {
        const errorMessage = response.error?.message || 'Failed to get response';
        updateMessage(assistantMessageId, {
          text: `Sorry, ${errorMessage.toLowerCase()}`,
          status: 'error',
        });
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Chat network error:', err);
      updateMessage(assistantMessageId, {
        text: 'Sorry, I couldn\'t connect to the server. Please check your connection and try again.',
        status: 'error',
      });
      setError('Network error - please check your connection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* FAB Button - hidden on mobile when chat is open */}
      <button
        onClick={toggleOpen}
        className={clsx(
          'fixed z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all',
          'right-4',
          // Safe-area handled via the `bottom` calc below — do NOT also add
          // padding-bottom, or the icon gets pushed off-center in the bubble.
          isOpen
            ? 'hidden sm:flex bg-slate-600 hover:bg-slate-700'
            : 'bg-primary-600 hover:bg-primary-700'
        )}
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window - full screen on mobile, fixed size on desktop */}
      {isOpen && (
        <div
          className="fixed inset-4 sm:inset-auto sm:bottom-24 sm:right-4 z-40 sm:w-96 sm:h-[32rem] max-h-[calc(100dvh-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
          style={{
            // Lift the window above the home indicator via `bottom` only.
            // (Don't also pad the bottom — that leaves a dead gap.)
            bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
            // Push the header below the notch on mobile (0 on desktop, so no
            // effect there). The window itself stays at inset-4.
            paddingTop: 'env(safe-area-inset-top, 0px)'
          }}
        >
          {/* Header */}
          <div className="bg-primary-600 text-white px-4 py-3 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Mise Assistant</h3>
              <p className="text-xs text-primary-200">{contextConfig.subtitle}</p>
            </div>
            <div className="flex items-center gap-1">
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=Mise Support Request`}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Email support"
              >
                <Mail className="w-4 h-4" />
              </a>
              {messages.length > 0 && (
                <button
                  onClick={clearMessages}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Clear chat"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={toggleOpen}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 ? (
              <div className="text-slate-500 py-2">
                {/* Loading state */}
                {insightsLoading && context.type === 'dashboard' ? (
                  <div className="text-center py-6">
                    <div className="flex gap-1 justify-center mb-3">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <p className="text-sm">Loading insights...</p>
                  </div>
                ) : (
                  <>
                    {/* Real-time alerts from insights */}
                    {insights && insights.alerts.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {insights.alerts.map((alert, i) => (
                          <div
                            key={i}
                            className={clsx(
                              'flex items-start gap-2 p-3 rounded-lg text-sm',
                              alert.type === 'critical' && 'bg-red-50 text-red-700 border border-red-200',
                              alert.type === 'warning' && 'bg-amber-50 text-amber-700 border border-amber-200',
                              alert.type === 'info' && 'bg-blue-50 text-blue-700 border border-blue-200'
                            )}
                          >
                            {alert.type === 'critical' && <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                            {alert.type === 'warning' && <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                            {alert.type === 'info' && <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                            <span>{alert.message}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Quick stats */}
                    {insights && insights.quickStats.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {insights.quickStats.map((stat, i) => (
                          <div key={i} className="bg-white rounded-lg p-2 text-center border border-slate-200">
                            <div className="text-lg font-semibold text-slate-900">{stat.value}</div>
                            <div className="text-xs text-slate-500">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Empty state message */}
                    {(!insights || insights.alerts.length === 0) && (
                      <div className="text-center mb-4">
                        <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">
                          {contextConfig.emptyMessage}
                        </p>
                      </div>
                    )}

                    {/* Dynamic suggestions from insights, or fallback to static */}
                    <div className="space-y-2">
                      {(insights?.suggestedActions || contextConfig.initialSuggestions).map((suggestion, i) => (
                        <button
                          key={i}
                          onClick={() => handleSend(suggestion)}
                          className="block w-full text-left px-3 py-2 bg-white rounded-lg border border-slate-200 text-sm text-slate-700 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))
            )}

            {/* Loading indicator */}
            {isLoading && messages[messages.length - 1]?.status === 'sending' && (
              <div className="flex items-center gap-2 text-slate-500 text-sm ml-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span>Thinking...</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error message */}
          {error && (
            <div className="px-4 py-2 bg-danger-50 text-danger-700 text-sm border-t border-danger-100">
              {error}
            </div>
          )}

          {/* Input */}
          <ChatInput
            onSend={handleSend}
            disabled={isLoading}
            suggestions={suggestions}
          />

          {/* Human support link */}
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-center">
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=Mise Support Request`}
              className="text-xs text-slate-500 hover:text-primary-600 transition-colors"
            >
              Need human help? Email us →
            </a>
          </div>
        </div>
      )}
    </>
  );
}
